import {
	Dialog,
	Form,
	FormattedText,
	FormItem,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ItemPicker,
	CommonItemPickerOptions,
	ItemPickerOptions,
	SharePointItemPickerOptions,
	WebDavItemPickerOptions,
	ItemPickerLocation,
	ItemPickerMode
} from "../ItemPicker/ItemPicker";
import {
	convertToDestination,
	ThingsToCopy
} from "client_legacy/Documents/MoveAndCopy/common";
import { CopyResult, DocumentStoreType } from "client/Api/Documents/common";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import { IFormHandler, useFormHandler } from "client/Forms";
import { getExtension, getFileNameWithoutExtension } from "client_legacy/Documents/path";
import { SharePointCopyImplementer } from "client_legacy/Documents/MoveAndCopy/sharePointCopyImplementer";
import { WebDavCopyImplementer } from "client_legacy/Documents/MoveAndCopy/webDavCopyImplementer";
import { isNotNilOrEmpty } from "client/Misc/utils";
import { TextInputWithExtension } from "./TextInputWithExtension";
import {
	convertServerErrors,
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	getIllegalCharacters,
	isValid as isSharePointNameValid
} from "client_legacy/Documents/SharePoint/documentNamePolicy";
import {
	isValid as isWebDavNameValid
} from "client_legacy/Documents/WebDav/documentNamePolicy";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export type CopyDialogProps = {
	onClose: () => void;
	thingsToCopy: ThingsToCopy;
};

export const CopyDialog = (props: CopyDialogProps): ReactElement => {
	const {
		thingsToCopy,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [location, setLocation] = useState<ItemPickerLocation>(
		(): ItemPickerLocation => thingsToCopy.source
	);

	const hasNameInput: boolean = thingsToCopy.documents.length === 1;

	const [formData, setFormData] = useState<FormData>(
		(): FormData => ({
			name: hasNameInput
				? getFileNameWithoutExtension(thingsToCopy.documents[0].name)
				: null
		})
	);

	const fileExtension: string = getExtension(thingsToCopy.documents[0].name);

	const alertManager: AlertManager = useAlerts();

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<FormData> => {

			let result: IValidationConfiguration<FormData> = staticValidationConfiguration;

			result = result.clone();

			if (!hasNameInput) {
				return result;
			}

			let illegalCharacters: readonly string[] = [];

			async function getCharacters(): Promise<void> {
				try {
					setIsBusy(true);

					const result: readonly string[] = await getIllegalCharacters();

					illegalCharacters = result;
					setIsBusy(false);
				} catch (e) {
					console.error(e);
					alertManager.showError("_GeneralErrorMessage");
					setIsBusy(false);
				}
			}

			getCharacters();

			const nameErrorMessage: string = localize("_FillInFileName");

			result.forScalar("name")
				.add(
					inline((value: string | null): InputValidationResult => {
						if (!value) {
							return {
								kind: InputValidationResultKind.Valid
							};
						}

						let errorMessage: string | null = null;

						if (!value.trim()) {
							errorMessage = nameErrorMessage;
						}

						switch (location.kind) {
							case DocumentStoreType.SharePoint:
								if (!isSharePointNameValid(value, illegalCharacters)) {
									errorMessage = localize("_ContainsIllegalCharacters")
										.replace("{0}", illegalCharacters.join(" "));
								}
								break;
							case DocumentStoreType.WebDav:
								if (!isWebDavNameValid(value)) {
									errorMessage = localize("_ContainsIllegalCharactersForWebDav");
								}
								break;
							default:
								never(location);
						}

						if (errorMessage) {
							return {
								kind: InputValidationResultKind.Invalid,
								errorMessage
							};
						}

						return {
							kind: InputValidationResultKind.Valid
						};
					})
						.validateOn("formSubmit")
						.fixErrorOn("inputFinished"),
					isNotNullNorUndefined(nameErrorMessage)
						.validateOn("formSubmit")
						.fixErrorOn("inputFinished"),
					isNotEmpty(nameErrorMessage)
						.validateOn("formSubmit")
						.fixErrorOn("inputFinished"),
				);

			return result;
		},
		[
			alertManager,
			hasNameInput,
			location
		]
	);

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const sharePointCopyImplementer = useMemo(
		(): SharePointCopyImplementer => {
			return new SharePointCopyImplementer();
		},
		[]
	);

	const webDavCopyImplementer = useMemo(
		(): WebDavCopyImplementer => {
			return new WebDavCopyImplementer();
		},
		[]
	);

	const options: ItemPickerOptions = useMemo(
		() => {
			const { sharePoint, webDav } = documentsConfiguration();

			const sharePointOptions: SharePointItemPickerOptions = sharePoint.isConfigured
				? {
					isEnabled: true,
					shouldShowPersonalViews: true,
					availableDestination: sharePointCopyImplementer.getEnabledView(thingsToCopy),
				}
				: { isEnabled: false };

			const webDavOptions: WebDavItemPickerOptions = webDav && webDav.isConfigured
				? {
					isEnabled: true,
					availableDestination: webDavCopyImplementer.getEnabledServer(thingsToCopy)
				}
				: { isEnabled: false };

			const commonOptions: CommonItemPickerOptions = {
				mode: ItemPickerMode.SingleFolder,
				creationOptions: {
					folder: true
				}
			};

			return {
				sharePointOptions,
				webDavOptions,
				commonOptions
			};
		},
		[
			sharePointCopyImplementer,
			thingsToCopy,
			webDavCopyImplementer
		]
	);

	const copyItemsHandler = useCallback(
		async (): Promise<void> => {
			try {
				setIsBusy(true);

				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate();

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				let result: CopyResult;
				switch (thingsToCopy.storeKind) {
					case DocumentStoreType.SharePoint:
						result = await sharePointCopyImplementer.copy(
							thingsToCopy,
							convertToDestination(location),
							isNotNilOrEmpty(formHandler.value.name)
								? formHandler.value.name + fileExtension
								: null,
							globalCancellation
						);
						break;
					case DocumentStoreType.WebDav:
						result = await webDavCopyImplementer.copy(
							thingsToCopy,
							convertToDestination(location),
							isNotNilOrEmpty(formHandler.value.name)
								? formHandler.value.name + fileExtension
								: null,
							globalCancellation
						);
						break;
					default:
						never(thingsToCopy);
				}

				switch (result.kind) {
					case "success":
						alertManager.showSuccess(localize("_FileHasBeenCopiedSuccessfully"));
						onClose();
						break;
					case "failed":
						throw result.errors;
					case "scheduled":
						alertManager.showInfo(
							<FormattedText
								text={localize("_StartedCopyingAmountItemsToFolderNameSeeNotificationsForAdditionalInformation")}
								thingsToReplace={[
									{ what: "{{Amount}}", filler: thingsToCopy.documents.length.toString() },
									{ what: "{{FolderName}}", filler: location.folder.name },
								]}
							/>
						);
						onClose();
						break;
					default:
						never(result);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					throw e;
				}

				setIsBusy(false);
				let wasHandled: boolean = false;

				if (Array.isArray(e) && e.length > 0) {
					formHandler.validation.setServerErrors(
						convertServerErrors(e)
					);
					wasHandled = true;
				} else if (formHandler.validation.tryProcessServerError(e)) {
					wasHandled = true;
				}

				if (!wasHandled) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}
		},
		[
			alertManager,
			fileExtension,
			formHandler.validation,
			formHandler.value
				.name,
			location,
			onClose,
			sharePointCopyImplementer,
			thingsToCopy,
			webDavCopyImplementer,
			globalCancellation
		]
	);

	return (
		<Dialog
			trackingId="copy-document-dialog"
			hasBusyIndicator={isBusy}
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={localize("_CopyTo")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<ItemPicker
					options={options}
					location={location}
					onLocationChange={setLocation}
				/>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				{hasNameInput && (
					<Form onSubmit="NONE" hasWidthConstrained>
						<FormItem
							label={localize("_DocumentName")}
							isRequired
						>
							<TextInputWithExtension
								attachable={formHandler.for("name")}
								isMobile={isMobile}
								fileExtension={fileExtension}
							/>
						</FormItem>
					</Form>
				)}
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={copyItemsHandler}
						text={localize("_CopyHere")}
						trackingId="copy-here"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

CopyDialog.displayName="CopyDialog";

const staticValidationConfiguration = createValidationConfiguration<FormData>();

type FormData = {
	name: string | null;
};

type DialogState = {
	isOpen: false;
} | {
	isOpen: true;
	thingsToCopy: ThingsToCopy;
};

type CopyDialogInitialData = Omit<CopyDialogProps, "onClose">;

export function useCopyDialog(): [
	dialogElement: ReactElement | null,
	open: (props: CopyDialogInitialData) => void
] {

	const [dialogState, setDialogState] = useState<DialogState>({isOpen: false});

	const renderDialog = useCallback(
		(): ReactElement | null => {
			return dialogState.isOpen
				? (
					<CopyDialog
						thingsToCopy={dialogState.thingsToCopy}
						onClose={()=>setDialogState({isOpen:false})}
					/>
				)
				: null;
		},
		[dialogState]
	);

	const openDialogHandler = useCallback(
		(props: CopyDialogInitialData): void => {
			setDialogState({
				isOpen: true,
				thingsToCopy: props.thingsToCopy
			});
		},
		[]
	);

	return [
		renderDialog(),
		openDialogHandler
	];
}